import {useEffect, useRef} from 'react';
import {useAtomValue} from 'jotai';
import {authStatusAtom, cachedSessionAtom, isOnlineAtom} from './authAtoms';
import {isAdminOrSuperAdmin} from '@/types/auth';

// Every lazy route in AppRoutes.tsx that sits behind AdminAuth — mirrors
// that file's own import() specifiers exactly (same paths/extensions)
// so this warms the identical chunk, not a second copy of it. Field-
// reachable routes (Daily Report/Run Tickets/Barrel Tests/Barrel Test
// Trend) are handled separately by useFieldRoutePrefetch.ts, triggered
// from WellsListPage itself rather than here — see that hook's own doc
// comment for why (Duane specifically wanted the trigger tied to "the
// user is online showing the wells page," not just auth resolving).
const ADMIN_ROUTE_IMPORTS = [
   () => import('@/pages/DashboardPage'),
   () => import('@/pages/ReportsPage'),
   () => import('@/pages/AuditReportPage'),
   () => import('@/pages/TanksAllocationsReportPage.tsx'),
   () => import('@/pages/Ogp03iExportPage.tsx'),
   () => import('@/pages/admin/UsersAdminPage.tsx'),
   () => import('@/pages/admin/WellsAdminPage.tsx'),
   () => import('@/pages/admin/WellEditPage.tsx'),
   () => import('@/pages/admin/LeaseAdminPage.tsx'),
];

/**
 * Warms React.lazy()'s module cache (see RouteErrorBoundary.tsx's own
 * doc comment for why that cache — success OR failure — is permanent
 * for the rest of this page load) for every Admin/SuperAdmin-only route,
 * the moment there's both a verified ('authenticated', not just
 * 'offline-cached') session of that role AND a real network connection.
 *
 * Scoped to Admin/SuperAdmin only — an Admin has no single guaranteed
 * landing page the way a field User has Wells (their default route is
 * Dashboard, but they can reach any Setup/Reports page directly too), so
 * this fires off auth resolving in general rather than any one page's
 * mount, unlike useFieldRoutePrefetch.ts. A field User's tab still never
 * fires any of these imports at all — preserves the original reason
 * these pages are code-split in the first place (see
 * code_splitting_and_prettier_2026_08_31 in project memory: a field
 * User's tab shouldn't download the Admin-only Setup/Reports bundle).
 *
 * Mount once near the root (App.tsx), alongside the app's other
 * always-on effects (useAuthBootstrap, useOfflineSync, etc.).
 */
export function useRoutePrefetch() {
   const authStatus = useAtomValue(authStatusAtom);
   const isOnline = useAtomValue(isOnlineAtom);
   const cachedSession = useAtomValue(cachedSessionAtom);
   const prefetchedRef = useRef(false);

   useEffect(() => {
      if (prefetchedRef.current) return;
      if (authStatus !== 'authenticated' || !isOnline || !cachedSession) return;
      if (!isAdminOrSuperAdmin(cachedSession.role)) return;
      prefetchedRef.current = true;

      void Promise.all(ADMIN_ROUTE_IMPORTS.map((load) => load().catch(() => {})));
   }, [authStatus, isOnline, cachedSession]);
}
